import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule, DatePipe, formatDate, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  FullCalendarComponent,
  FullCalendarModule,
} from '@fullcalendar/angular';
import kaLocale from '@fullcalendar/core/locales/ka';
import momentPlugin, { toMoment } from '@fullcalendar/moment';
import {
  BookingService,
  EmployeeService,
  isEmoji,
  transformDateTime,
  UserService,
  VisitStatusPipe,
} from '@beautyapp/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzDrawerModule, NzDrawerService } from 'ng-zorro-antd/drawer';
import { Store } from '@ngxs/store';
import { AuthState } from '@beautyapp/auth';
import {
  CalendarTimeInterval,
  ClearAddAppointmentSpecService,
  ClearAddEmployeeAppointment,
} from '../../appointment.actions';
import {
  AppointmentState,
  computeCalendarBookingAmounts,
} from '../../appointment.state';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { NgxSpinnerService } from 'ngx-spinner';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import { ActivatedRoute } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { AddAppointmentComponent } from '../../booking-modal';
import { VisitManageComponent } from '../../visit-modal/visit-manage.component';
import { EventClickArg } from '@fullcalendar/core';
import { addDays, format } from 'date-fns';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzSpaceModule, NzSpaceCompactComponent } from 'ng-zorro-antd/space';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { DatepickerComponent } from '@beautyapp/form';

@Component({
  selector: 'beautyapp-employee-day',
  templateUrl: './employee-day.component.html',
  styleUrls: ['./employee-day.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    FullCalendarModule,
    NzLayoutModule,
    NzGridModule,
    NzSpaceModule,
    NzSpaceCompactComponent,
    NzButtonModule,
    NzIconModule,
    NzDrawerModule,
    NzPopoverModule,
    NzTooltipDirective,
    DatePipe,
    VisitStatusPipe,
    DatepickerComponent,
  ],
})
export class EmployeeDayComponent implements OnInit {
  private employeeService = inject(EmployeeService);
  private userService = inject(UserService);
  private bookingService = inject(BookingService);
  private modalService = inject(NzModalService);
  private drawerService = inject(NzDrawerService);
  private store = inject(Store);
  private spinner = inject(NgxSpinnerService);
  private _location = inject(Location);
  private route = inject(ActivatedRoute);
  private translateService = inject(TranslateService);
  visitBookingEnabled = false;
  // beaut-365: day-form slot granularity, shared/persisted via AppointmentState
  // (same pattern as the day-grid/online-booking calendars). Specialists can pick
  // 5/15/20/30/60 min so the day form isn't locked to 15.
  timeInterval?: string = '00:15:00';

  @ViewChild('popoverElementRef', { read: ElementRef })
  popoverElementRef!: ElementRef;
  fromRoute = false;
  employeeId: any;
  userId: any;
  currentDate?: string;
  startTime!: Date;
  events: any;
  eventsModel: any;
  @ViewChild('calendar') calendarComponent!: FullCalendarComponent;
  calendarOptions: any = {
    initialView: 'timeGrid',
    views: {
      timeGridSevenDays: {
        type: 'timeGrid',
        duration: { days: 7 },
      },
    },
    locales: [kaLocale],
    locale: 'ka',
    timeZone: 'Asia/Tbilisi',
    plugins: [
      timeGridPlugin,
      interactionPlugin,
      momentPlugin,
      momentTimezonePlugin,
    ],
    editable: false,
    selectable: true,
    nowIndicator: true,
    height: '100%',
    eventClick: this.handleEventClick.bind(this),
    dateClick: this.handleDateClick.bind(this),
    select: this.handleDateSelect.bind(this),
    slotLabelInterval: '01:00',
    slotDuration: this.timeInterval,
    headerToolbar: false,
    slotLabelFormat: [
      {
        hour: '2-digit',
        minute: '2-digit',
        omitZeroMinute: false,
        meridiem: false,
        hour12: false,
      },
    ],
    dayHeaderFormat: {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      omitCommas: true,
    },
    allDaySlot: false,
    slotMinTime: '06:00:00',
    slotMaxTime: '24:00:00',
    // eventMouseEnter: this.handleEventMouseEnter.bind(this),
    // eventMouseLeave: this.handleEventMouseLeave.bind(this),
  };

  ngOnInit(): void {
    console.log(this.userId);
    if (!this.userId) {
      const getActiveUser: any = this.store.selectSnapshot(
        AuthState.getProfile
      );
      this.userId = getActiveUser.id;
    }

    this.route.params.subscribe((route: any) => {
      console.log(route);
      if (route && route.userId) {
        this.userId = route.userId;
        this.fromRoute = true;
      }
    });

    this.getUserById();

    this.spinner.show('bookings');
    this.currentDate = format(new Date(), 'yyyy-MM-dd');

    this.store.select(AuthState.getActiveBranch).subscribe((res: any) => {
      const startTime = `0${res.whStart}`.slice(-2);
      const endTime = `0${res.whEnd}`.slice(-2);
      this.calendarOptions.slotMinTime = `${startTime}:00:00`;
      this.calendarOptions.slotMaxTime = `${endTime}:00:00`;
      this.visitBookingEnabled = res?.visitBookingEnabled || false;
    });

    // beaut-365: apply the persisted day-form interval (shared across calendars).
    this.store
      .select(AppointmentState.getCalendarInterval)
      .subscribe((res: any) => {
        if (res) {
          this.timeInterval = res;
          this.calendarOptions.slotDuration = res;
        }
      });
  }

  // beaut-365: change the day-form slot granularity and persist it via the store.
  changeTimeInterval(timeInterval: string): void {
    this.store.dispatch(new CalendarTimeInterval(timeInterval));
    this.timeInterval = timeInterval;
    this.calendarOptions.slotDuration = timeInterval;
  }

  ngAfterViewInit(): void {
    this.getCalendarBooking();
  }

  getUserById() {
    this.userService.getById(this.userId).subscribe((res) => {
      console.log(res);
      this.employeeId = res?.employee?.id;
    });
  }

  nextDate() {
    const calendarApi = this.calendarComponent.getApi();
    calendarApi.next();
    this.currentDate = format(
      calendarApi.getCurrentData().currentDate,
      'yyyy-MM-dd'
    );
    this.getCalendarBooking();
  }

  prevDate() {
    const calendarApi = this.calendarComponent.getApi();
    calendarApi.prev();
    this.currentDate = format(
      calendarApi.getCurrentData().currentDate,
      'yyyy-MM-dd'
    );
    this.getCalendarBooking();
  }

  changeDate($event: any) {
    const calendarApi = this.calendarComponent.getApi();
    calendarApi.gotoDate(new Date($event));
    this.currentDate = format($event, 'yyyy-MM-dd');
    this.getCalendarBooking();
    this.calendarComponent.getApi().render();
  }

  currenDate() {
    const calendarApi = this.calendarComponent.getApi();
    calendarApi.gotoDate(new Date());
    this.currentDate = format(new Date(), 'yyyy-MM-dd 00:00:00');
    this.getCalendarBooking();
    this.calendarComponent.getApi().render();
  }

  getCalendarBooking(dayGrid = 'timeGrid') {
    this.spinner.show('bookings');
    const currentStart = format(
      new Date(this.calendarComponent.getApi().view.activeStart),
      'yyyy-MM-dd'
    );
    const currentEnd = format(
      new Date(this.calendarComponent.getApi().view.activeEnd),
      'yyyy-MM-dd'
    );

    const params = {
      userId: this.userId,
      date: this.currentDate,
      dateRange: [currentStart, currentEnd],
    };
    this.bookingService
      .getCalendarEmployeeBookingNew(params)
      .subscribe((res: any[]) => {
        const mapBooking = res.map((m: any) => {
          const services = m.services
            ?.map((s: any) => s.service?.name || s.name)
            ?.join(', ');
          // beaut-324: derive the amount from per-procedure lastPrice (sum),
          // not the denormalized booking-level price which collapses to a
          // single discounted procedure. Same helper the main calendar uses.
          const { price: calcPrice, salePrice: calcSalePrice } =
            computeCalendarBookingAmounts(m);
          return {
            id: m.id,
            groupId: m.visit?.id,
            oldStyle: !!m.visit?.id,
            resourceId: m.employeeId,
            start: transformDateTime(m.bookingDate, m.startTime),
            end: transformDateTime(m.bookingDate, m.endTime),
            title: m.visit?.isAnonymous
              ? m.visit?.anonymousName + '(უცნობი)'
              : m.user?.fullName,
            backgroundColor:
              m.service?.serviceCategory?.color ||
              m.bookingServices?.[0]?.service?.serviceCategory?.color ||
              null,
            displayEventTime: true,
            extendedProps: {
              bookingDate: m.bookingDate,
              employeeName: m.employee?.user?.fullName,
              fullName: m.user?.fullName,
              mobilePhone: m.user?.mobilePhone,
              userId: m.user?.id,
              isAnonymous: m.visit?.isAnonymous,
              anonymousName: m.visit?.anonymousName
                ? m.visit?.anonymousName + '(უცნობი)'
                : null,
              services: services,
              price: calcPrice,
              salePrice: calcSalePrice,
              salePercent: m.salePercent,
              description: m.description,
              latestComment: m.latestComment || null,
              commentCount: m.commentCount || 0,
              status: m.status,
              visitStatus: m.visit?.status || null,
              onUserRequest: m.onUserRequest,
              roomName: m.room?.name,
              source: m.source,
              balance: m.user?.balance,
              createdAt: m.createdAt,
              createdBy: m.createdBy?.fullName,
              completedBy: m.completedBy?.fullName,
              onlineBookingId: m.onlineBookingId,
            },
          };
        });
        console.log(mapBooking);
        const calendarApi = this.calendarComponent.getApi();
        this.calendarOptions.events = mapBooking;
        this.calendarOptions.eventDidMount = (mountArg: any) => {
          if (mountArg.event.extendedProps['icon']) {
            mountArg.el
              .querySelector('.fc-event-time')
              ?.classList.add('fas', 'fa-lock');
          }
        };

        setTimeout(() => calendarApi.render(), 0);

        this.spinner.hide('bookings');
      });
  }

  handleEventClick(clickInfo: EventClickArg) {
    const oldStyle = clickInfo.event.extendedProps['oldStyle'];
    const groupId = clickInfo.event.groupId;

    if (oldStyle && groupId) {
      const modal = this.drawerService.create({
        nzClosable: true,
        nzMaskClosable: false,
        nzTitle: this.translateService.instant('calendar.planVisit'),
        nzContent: VisitManageComponent,
        nzWidth: window.innerWidth <= 1024 ? '100%' : '70%',
        nzBodyStyle: { padding: '0' },
        nzData: {
          visitId: groupId,
        },
      });
      modal.afterClose.subscribe(() => {
        this.store.dispatch(new ClearAddAppointmentSpecService());
        const calendarApi = this.calendarComponent.getApi();
        this.currentDate = format(
          calendarApi.getCurrentData().currentDate,
          'yyyy-MM-dd'
        );
        this.getCalendarBooking();
        setTimeout(() => this.calendarComponent.getApi().render(), 0);
      });
    } else {
      const modal = this.modalService.create({
        nzClosable: false,
        nzMaskClosable: false,
        nzTitle: this.translateService.instant('calendar.planVisit'),
        nzContent: AddAppointmentComponent,
        nzWidth: '1100px',
        nzStyle: { top: '0px' },
        nzBodyStyle: { padding: '0' },
        nzData: {
          employeeId: this.employeeId,
          bookingId: clickInfo.event.id,
        },
      });
      modal.afterClose.subscribe(() => {
        this.store.dispatch(new ClearAddEmployeeAppointment());
        const calendarApi = this.calendarComponent.getApi();
        this.currentDate = format(
          calendarApi.getCurrentData().currentDate,
          'yyyy-MM-dd'
        );
        this.getCalendarBooking();
        setTimeout(() => this.calendarComponent.getApi().render(), 0);
      });
    }
  }

  handleDateClick(clickInfo: any) {
    // console.log(clickInfo);
    // const dateStr = clickInfo.dateStr;
    // const modal = this.modalService.create({
    //   nzClosable: false,
    //   nzMaskClosable: false,
    //   nzTitle: 'ვიზიტის დაგეგმვა',
    //   nzContent: AddEmployeeAppointmentComponent,
    //   nzWidth: '1100px',
    //   nzStyle: { top: '0px' },
    //   nzBodyStyle: { padding: '0' },
    //   nzData: {
    //     date: dateStr
    //   }
    // });
    //
    // modal.afterClose.subscribe(res => {
    //   console.log(res);
    //   this.store.dispatch(new ClearAddEmployeeAppointment());
    //   const calendarApi = this.calendarComponent.getApi();
    //   this.getCalendarBooking({ date: toMoment(new Date(dateStr), calendarApi).format('YYYY-MM-DD') });
    //   setTimeout(() => this.calendarComponent.getApi().handleRenderRequest(), 0);
    // });
  }

  handleDateSelect(clickInfo: any) {
    const startStr = clickInfo.startStr;
    console.log(startStr);

    if (this.visitBookingEnabled) {
      const modal = this.drawerService.create({
        nzClosable: true,
        nzMaskClosable: false,
        nzTitle: this.translateService.instant('calendar.planVisit'),
        nzContent: VisitManageComponent,
        nzWidth: window.innerWidth <= 1024 ? '100%' : '70%',
        nzBodyStyle: { padding: '0' },
        nzData: {
          employeeId: this.employeeId,
          date: startStr,
        },
      });
      modal.afterClose.subscribe(() => {
        this.store.dispatch(new ClearAddAppointmentSpecService());
        this.currentDate = format(new Date(startStr), 'yyyy-MM-dd');
        this.getCalendarBooking();
        setTimeout(() => this.calendarComponent.getApi().render(), 0);
      });
    } else {
      const modal = this.modalService.create({
        nzClosable: false,
        nzMaskClosable: false,
        nzTitle: this.translateService.instant('calendar.planVisit'),
        nzContent: AddAppointmentComponent,
        nzWidth: '1100px',
        nzStyle: { top: '0px' },
        nzBodyStyle: { padding: '0' },
        nzData: {
          date: startStr,
          employeeId: this.employeeId,
        },
      });
      modal.afterClose.subscribe(() => {
        this.store.dispatch(new ClearAddAppointmentSpecService());
        this.currentDate = format(new Date(startStr), 'yyyy-MM-dd');
        this.getCalendarBooking();
        setTimeout(() => this.calendarComponent.getApi().render(), 0);
      });
    }
  }

  dayGridChange(dayGrid: string) {
    // console.log(dayGrid);
    let date: any = format(new Date(), 'yyyy-MM-dd');
    if (dayGrid === 'timeGridWeek') {
      date = {
        start: format(new Date(), 'yyyy-MM-dd'),
        end: format(new Date(addDays(new Date(), 7)), 'yyyy-MM-dd'),
      };
    }
    this.calendarComponent.getApi().changeView(dayGrid, date);
    this.currenDate();
  }

  back() {
    this._location.back();
  }

  protected readonly isEmoji = isEmoji;
}
